import { navigate } from '@base-app/web2'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import type { CollaboratorFunction } from '../../../graphql/collaboratorFunctions/types'
import { Button, Checkbox, ErrorMessage, NavLink } from '../../atoms'

interface FunctionCheckboxesProps {
  functions: CollaboratorFunction[]
  selected: string[]
  onToggle: (key: string) => void
  loading: boolean
  failed: boolean
  // Asks for the list again after a load failure
  onRetry: () => void
  // Where "manage functions" leads when the application has none yet
  manageHref: string
  // The form is still loading its record
  skeleton?: boolean
}

// The function checkboxes of the collaborator form: value = key, label = name
export const FunctionCheckboxes = memo(
  ({
    functions,
    selected,
    onToggle,
    loading,
    failed,
    onRetry,
    manageHref,
    skeleton,
  }: FunctionCheckboxesProps) => {
    const { t } = useTranslation('admin-mobifull')
    if (loading || skeleton) {
      return (
        <div className="checkbox-group__options" aria-busy="true">
          <Checkbox skeleton />
          <Checkbox skeleton />
        </div>
      )
    }
    if (failed) {
      return (
        <div role="alert">
          <ErrorMessage message={t('collaborators.form.functionsError')} />
          <Button type="button" variant="secondary" onClick={() => onRetry()}>
            {t('common.actions.retry')}
          </Button>
        </div>
      )
    }
    if (functions.length === 0) {
      return (
        <p className="form-hint">
          {t('collaborators.form.functionsEmpty')}{' '}
          <NavLink
            href={manageHref}
            label={t('collaborators.form.functionsManage')}
            onClick={e => {
              e.preventDefault()
              navigate(manageHref)
            }}
          />
        </p>
      )
    }
    return (
      <div className="checkbox-group__options">
        {functions.map(fn => (
          <label key={fn.key} htmlFor={`fn-${fn.key}`} className="checkbox-label">
            <Checkbox
              id={`fn-${fn.key}`}
              checked={selected.includes(fn.key)}
              onChange={() => onToggle(fn.key)}
            />
            {fn.name}
          </label>
        ))}
      </div>
    )
  },
)

FunctionCheckboxes.displayName = 'FunctionCheckboxes'
